import React from 'react';

import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Grid from '@mui/material/Grid';
import ArticleIcon from '@mui/icons-material/Article';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import OutlinedInput from '@mui/material/OutlinedInput';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Moment from 'moment';
import 'moment/locale/pt-br';

import { useTheme } from '@mui/material/styles';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '../hooks/useQuery';
import { useSnackbar } from '../hooks/useSnackbar';
import { isCnpj, serializeQueryString } from '../utils';
import CustomChip from '../components/CustomChip';

let debounce = null;

const Extrato = () => {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const { user, isUsuarioSebrae } = useAuth();
  const { buscarExtratos, listarFornecedores } = useQuery();

  const [extratos, setExtratos] = React.useState([]);
  const datePickerRef = React.useRef();
  const [loading, setLoading] = React.useState(false);
  const [dataInicio, setDataInicio] = React.useState(Moment().startOf('year'));
  const [dataFim, setDataFim] = React.useState(Moment().endOf('year'));
  const [queryParams, setQueryParams] = React.useState({
    cnpj: isUsuarioSebrae
      ? ''
      : user.cnpj
      ? user.cnpj.replace(/[^a-zA-Z0-9]/g, '')
      : user.cpf,
    data_inicio: Moment().startOf('year').format('YYYY-MM-DD'),
    data_fim: Moment().endOf('year').format('YYYY-MM-DD'),
    numero_nf: '',
  });
  const [loadingAutocomplete, setLoadingAutocomplete] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [fornecedor, setFornecedor] = React.useState(null);
  const [runRequest, setRunRequest] = React.useState(false);

  React.useEffect(() => {
    if (datePickerRef) {
      datePickerRef.current.children[0].classList.remove('Mui-error');
      datePickerRef.current.children[1].classList.remove('Mui-error');
    }
  }, [datePickerRef]);

  React.useEffect(() => {
    listarExtratos();
  }, [runRequest]);

  const listarExtratos = async () => {
    try {
      setLoading(true);
      const { data } = await buscarExtratos(serializeQueryString(queryParams));
      setExtratos(data);
    } catch (error) {
      snackbar.showSnackbar(error.toString());
    } finally {
      setLoading(false);
    }
  };

  const fornecedores = async (term) => {
    setLoadingAutocomplete(true);
    const newTerm = isCnpj(term) ? term.replace(/[^a-zA-Z0-9]/g, '') : term;

    const result = await listarFornecedores(newTerm);
    if (result && result.data && result.data.data) {
      const arr = result.data.data.map((item) => ({
        ...item,
        cnpj_sem_pontos: item.cnpj_fornec.replace(/\D/g, ''),
      }));
      setOptions(arr);
    }
    setLoadingAutocomplete(false);
  };

  const handleChangeInicio = (value) => {
    setDataInicio(value);
    setQueryParams({ ...queryParams, data_inicio: value.format('YYYY-MM-DD') });
  };

  const handleChangeFim = (value) => {
    setDataFim(value);
    setQueryParams({ ...queryParams, data_fim: value.format('YYYY-MM-DD') });
  };

  const onChangeAutoComplete = (event) => {
    if (event.target.value.length > 0) {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        fornecedores(event.target.value);
      }, 1000);
    } else {
      setOptions([]);
    }
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) =>
      option.cnpj_sem_pontos + option.nome_fornec + option.cnpj_fornec,
  });

  return (
    <Box
      mt={8}
      sx={{ width: '100%', background: '#FAFCFF', minHeight: '100vh' }}
    >
      <AppBar position="static" color="primary" style={{ padding: 10 }}>
        <Grid container spacing={0}>
          <Grid item xs display="flex" alignItems="center">
            <ArticleIcon sx={{ fontSize: 40 }} />
            <span style={{ color: 'white', fontSize: 20 }}>Extratos</span>
          </Grid>
        </Grid>
      </AppBar>

      <Grid container spacing={1} paddingY="2rem" paddingX="1rem">
        {isUsuarioSebrae && (
          <Grid item xs={12}>
            <Box>
              <Autocomplete
                noOptionsText="Nenhuma opção encontrada"
                loadingText="Carregando..."
                getOptionLabel={(option) =>
                  `${option.nome_fornec} - ${option.cnpj_fornec}`
                }
                options={options}
                id="controlled-demo"
                fullWidth
                value={fornecedor}
                loading={loadingAutocomplete}
                filterOptions={filterOptions}
                onChange={(event, newValue, reason) => {
                  if (reason === 'clear') {
                    setFornecedor(null);
                    setDataInicio(Moment().startOf('year'));
                    setDataFim(Moment().endOf('year'));
                    setExtratos([]);
                    setQueryParams({
                      ...queryParams,
                      numero_nf: '',
                      cnpj: '',
                    });
                    setRunRequest(!runRequest);
                    return;
                  }
                  setFornecedor(newValue);
                  setQueryParams({
                    ...queryParams,
                    cnpj: newValue.cnpj_fornec.replace(/\D/g, ''),
                  });
                  setRunRequest(!runRequest);
                }}
                renderInput={(params) => (
                  <TextField
                    onChange={onChangeAutoComplete}
                    {...params}
                    label="Fornecedor Nome ou CNPJ"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loadingAutocomplete ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
        )}
      </Grid>
      <Grid
        container
        padding={2}
        direction={{ xs: 'column-reverse', md: 'row', lg: 'row' }}
      >
        <Grid item xs={12} md="9" width="100%">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress style={{ marginTop: 60 }} />
            </Box>
          ) : (
            <div
              style={{
                width: '100%',
                overflowX: 'scroll',
                overflowY: 'hidden',
              }}
            >
              <table className="app_table">
                <thead
                  style={{
                    color: theme.palette.primary.main,
                    borderTop: `8px solid ${theme.palette.primary.light}`,
                  }}
                >
                  <tr>
                    <th>Data transação</th>
                    <th>Nota Fiscal</th>
                    <th>Status</th>
                    <th>Ordem de Serviço</th>
                    <th>Data Prevista Pagamento</th>
                    <th>Forma de pagamento</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {extratos.length === 0 ? (
                    <tr>
                      <td style={{ textAlign: 'center' }} colSpan={7}>
                        Nenhum cadastro encontrado.
                      </td>
                    </tr>
                  ) : (
                    extratos.map((extrato, index) => (
                      <tr
                        key={`${index}-extrato-key`}
                        style={{
                          background: index % 2 === 0 ? 'white' : '#f0eded',
                        }}
                      >
                        <td style={{ paddingLeft: '1.2rem' }}>
                          {Moment(extrato.data_transacao).format('DD/MM/YYYY')}
                        </td>
                        <td style={{ paddingLeft: '1.2rem' }}>
                          {extrato.titulo}
                        </td>
                        <td style={{ paddingLeft: '1.2rem' }}>
                          {extrato.situacao}
                          {/* <CustomChip
                            label={extrato.situacao}
                            status={extrato.situacao}
                          /> */}
                        </td>
                        <td style={{ paddingLeft: '1.2rem' }}>
                          {extrato.num_ordem}
                        </td>
                        <td style={{ paddingLeft: '1.2rem' }}>
                          {extrato.data_previsao_formatado}
                        </td>
                        <td style={{ paddingLeft: '1.2rem' }}>
                          {extrato.forma_pagamento}
                        </td>
                        <td style={{ paddingLeft: '1.2rem' }}>
                          {extrato.valor_formatado}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Grid>

        <Grid
          item
          sm={3}
          sx={{
            paddingLeft: { xs: 2 },
            paddingRight: { xs: 2 },
          }}
        >
          <Card
            variant="outlined"
            style={{
              borderTop: `8px solid ${theme.palette.primary.light}`,
            }}
          >
            <CardContent>
              <p
                style={{
                  color: '#0e72b8',
                  marginTop: 0,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FilterAltIcon color="primary" />
                Filtro
              </p>
              <p style={{ color: '#0e72b8', fontWeight: 'bold' }}>Nº da Nota</p>
              <TextField
                style={{ width: '100%', marginBottom: '0.8rem' }}
                id="outlined-basic"
                label="Número"
                value={queryParams.numero_nf}
                type="number"
                onInput={(e) => {
                  e.target.value = e.target.value
                    .substring(0, 16)
                    .replace(/[^0-9.]/g, '');
                }}
                inputProps={{
                  min: 0,
                  maxLength: 16,
                }}
                onChange={({ target }) =>
                  setQueryParams({ ...queryParams, numero_nf: target.value })
                }
                variant="outlined"
              />
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Box>
                  <MobileDatePicker
                    ref={datePickerRef}
                    label="Data início transação"
                    inputFormat="DD/MM/YYYY"
                    value={dataInicio}
                    onChange={handleChangeInicio}
                    renderInput={(params) => (
                      <TextField
                        error={false}
                        style={{ width: '100%' }}
                        {...params}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ marginTop: 2 }}>
                  <MobileDatePicker
                    ref={datePickerRef}
                    label="Data fim transação"
                    inputFormat="DD/MM/YYYY"
                    value={dataFim}
                    onChange={handleChangeFim}
                    renderInput={(params) => (
                      <TextField
                        error={false}
                        style={{ width: '100%' }}
                        {...params}
                      />
                    )}
                  />
                </Box>
              </LocalizationProvider>

              <Button
                variant="contained"
                onClick={() => {
                  if (!queryParams.cnpj) {
                    snackbar.showSnackbar(
                      'Selecione um fornecedor antes de buscar.',
                    );
                    return;
                  }
                  setRunRequest(!runRequest);
                }}
                style={{ width: '100%', marginTop: 20 }}
              >
                Buscar
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setDataInicio(Moment().startOf('year'));
                  setDataFim(Moment().endOf('year'));
                  setQueryParams({
                    ...queryParams,
                    numero_nf: '',
                  });

                  setRunRequest(!runRequest);
                }}
                style={{ width: '100%', marginTop: 20 }}
              >
                Limpar
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Extrato;
